/**
 * AgentHierarchyPanel — the room's live agent TREE, in one box.
 *
 * Mirrors the architect's BuildActivityPanel pattern (studied, not reinvented): ONE fixed shared
 * reader pane on top + a non-expanding roster below. The difference is the roster here is a TREE —
 * the generative network's org-structure (root → leads → specialists → reducer) drawn with ├─/└─/│
 * guide lines + indentation, so the user sees WHICH layer is executing and each agent's parent.
 *
 * Design (locked with the user):
 *  - Every roster row is a LINE, never expands. Far-left spinner for running AND awaiting (so the
 *    user can scan "what needs me" fast, exactly like the architect roster's left status slot).
 *  - Click any row → its content loads into the single shared pane above (one at a time). No inline
 *    panel expansion — that clutter is the whole point of this rewrite.
 *  - The roster is event-accreted (reducer's run-level ``agentRoster``): rows appear as their
 *    AppSync events arrive; the route is dynamic so nothing is predeclared.
 *
 * Builder controls (Stop-per-row / awaiting band) are wired by the parent room in Step 4 via the
 * ``renderRowAction`` render-prop + ``onSelect`` — this component owns layout + selection only.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check, Loader2, Circle, AlertTriangle, Clock, GripHorizontal,
  Brain, ChevronDown, ChevronRight, Sparkles, MinusCircle, Copy,
  Compass, GitMerge, User,
} from 'lucide-react';
import { formatAgentMessageForCopy } from '@/utils/agentMessageFormatting';
import { flattenAgentTree, engineStageLabel } from '@stepflow/ui-shared/utils/agentTree.js';
import { humanizeAgentName } from '@stepflow/ui-shared/utils/agentDisplayName.js';
import { useResizable } from '@stepflow/ui-shared/hooks/useResizable';
import { MODEL_ID_TO_FAMILY, MODEL_ROWS } from '@stepflow/ui-shared/services/generatedPricing';
import AgentMessageContent from './AgentMessageContent.jsx';
import AgentResponsePanel from './AgentResponsePanel.jsx';
import BuilderStreamTerminal from './BuilderStreamTerminal.jsx';
import BuilderLogCard from './BuilderLogCard.jsx';
import PromptCaptureLink from './PromptCaptureLink.jsx';

// ─── Usage chip (right-aligned, per-row) — same shape as BuildActivityPanel.LaneUsage ───────────
function _modelTag(modelId) {
  const s = String(modelId || '').toLowerCase();
  if (s.includes('opus')) return 'Opus';
  if (s.includes('sonnet')) return 'Sonnet';
  if (s.includes('haiku')) return 'Haiku';
  return '';
}
function _modelLabel(modelId) {
  if (!modelId) return '';
  const short = String(modelId)
    .replace(/^global\.anthropic\./, '')
    .replace(/^(us|eu|anthropic)\.anthropic\./, '')
    .replace(/^anthropic\./, '')
    .replace(/-\d{8}-v\d.*$/, '')
    .replace(/-/g, ' ');
  if (short) return short;
  const fam = MODEL_ID_TO_FAMILY[modelId];
  return fam ? (MODEL_ROWS[fam]?.label ?? fam) : modelId;
}
function _fmtTokens(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(v);
}

/** Per-row usage chip; falls back to the status word when there's no usage yet. */
function LaneUsage({ usage, fallback, fallbackClassName = 'text-slate-400' }) {
  if (!usage || typeof usage !== 'object') {
    return fallback ? <span className={`ml-auto shrink-0 text-[10px] ${fallbackClassName}`}>{fallback}</span> : null;
  }
  const cost = Number(usage.estimatedCostUsd) || 0;
  const points = Number(usage.points) || 0;
  const tag = _modelTag(usage.modelId);
  return (
    <span className="ml-auto flex shrink-0 items-center gap-1 text-[10px] tabular-nums text-slate-400 font-mono">
      {tag && <span className="text-slate-500">{tag}</span>}
      {cost > 0 && <span>{cost >= 0.01 ? `$${cost.toFixed(2)}` : `$${cost.toFixed(4)}`}</span>}
      {points > 0 && <span className="text-slate-500">{points.toLocaleString()} pts</span>}
    </span>
  );
}

/** Full breakdown bar under the reader (matches AgentResponsePanel.UsageStatusBar, compact). */
function UsageBar({ usage }) {
  if (!usage || typeof usage !== 'object') return null;
  const inT = Number(usage.inputTokens) || 0;
  const outT = Number(usage.outputTokens) || 0;
  const cacheRead = Number(usage.cacheReadTokens) || 0;
  const cacheCreation = Number(usage.cacheCreationTokens) || 0;
  const totalIn = inT + cacheRead + cacheCreation;
  const cost = Number(usage.estimatedCostUsd) || 0;
  const points = Number(usage.points) || 0;
  if (totalIn === 0 && outT === 0 && points === 0) return null;
  const parts = [];
  const label = _modelLabel(usage.modelId);
  if (label) parts.push(label);
  if (cost > 0) parts.push(`$${cost.toFixed(4)}`);
  parts.push(`↓${_fmtTokens(totalIn)}${cacheRead > 0 ? ` (${_fmtTokens(cacheRead)} cached)` : ''} ↑${_fmtTokens(outT)}`);
  if (usage.calls > 1) parts.push(`${usage.calls} calls`);
  if (points > 0) parts.push(`${points.toLocaleString()} pts`);
  return (
    <div className="border-t border-slate-200 px-3 py-1 text-[10px] tabular-nums text-slate-500 font-mono flex items-center gap-1 whitespace-nowrap overflow-x-auto">
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-slate-300">|</span>}
          <span>{p}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

/** A summed engine-stage cost line ("All Leads · Haiku · $0.03 · 92 pts"). Renders nothing when
 *  the stage had no usage (e.g. a run with no descent → no reducer calls). */
function StageCostLine({ label, usage }) {
  if (!usage) return null;
  const cost = Number(usage.cost) || 0;
  const points = Number(usage.points) || 0;
  const calls = Number(usage.calls) || 0;
  if (cost === 0 && points === 0) return null;
  const model = _modelLabel(usage.modelId);
  return (
    <div className="flex items-center gap-1.5 px-3 py-1 text-[10px] tabular-nums text-slate-500 font-mono">
      <span className="text-slate-600">{label}</span>
      {calls > 0 && <span className="text-slate-400">×{calls}</span>}
      <span className="ml-auto flex items-center gap-1">
        {model && <span className="text-slate-500">{model}</span>}
        <span>{cost >= 0.01 ? `$${cost.toFixed(2)}` : `$${cost.toFixed(4)}`}</span>
        {points > 0 && <span className="text-slate-500">{points.toLocaleString()} pts</span>}
      </span>
    </div>
  );
}

/** Sum $ + points across every roster row's usage + the two engine stages + the Summary — the
 *  whole-run cost. ``rows`` are the agent rows (workers/builders, each with row.usage);
 *  ``stageCost`` = {lead, reducer}; ``summaryUsage`` = the final synthesis usage. */
function computeRunTotal(rows, stageCost, summaryUsage) {
  let cost = 0;
  let points = 0;
  const add = (u) => {
    if (!u) return;
    cost += Number(u.estimatedCostUsd ?? u.cost) || 0;
    points += Number(u.points) || 0;
  };
  // AGENT rows only. Engine-STAGE rows (Group Lead / Summarizer) now carry their own per-call
  // ``usage`` for the per-row chip — but their spend is ALSO summed authoritatively into
  // ``stageCost.lead`` / ``stageCost.reducer`` below. Counting both double-charged the run
  // (the "$0.74" that looked wrong). So exclude stage rows here; ``stageCost`` is their single
  // source of truth for the total.
  for (const r of (rows || [])) { if (r && !r.isEngineStage) add(r.usage); }
  if (stageCost) { add(stageCost.lead); add(stageCost.reducer); }
  add(summaryUsage);
  return { cost, points };
}

// ─── Tree guide-lines ────────────────────────────────────────────────────────
/** Draw the ├─/└─/│ prefix for a tree row from its depth + ancestor "isLast" flags.
 *  Fixed-width monospace cells keep every level aligned regardless of name length. */
function TreeGuide({ depth, isLast, ancestorsLast }) {
  if (depth === 0) return null;
  const cells = [];
  // One vertical-guide cell per ANCESTOR level (skip the root's own column): a │ where that
  // ancestor still has siblings below it, a blank where it was the last child.
  for (let d = 1; d < depth; d += 1) {
    const ancestorWasLast = ancestorsLast[d];
    cells.push(
      <span key={d} className="inline-block w-3 text-center text-slate-300 select-none" aria-hidden>
        {ancestorWasLast ? ' ' : '│'}
      </span>,
    );
  }
  // This row's own elbow.
  cells.push(
    <span key="elbow" className="inline-block w-3 text-center text-slate-300 select-none" aria-hidden>
      {isLast ? '└' : '├'}
    </span>,
  );
  return <span className="flex shrink-0 font-mono text-[11px] leading-none">{cells}</span>;
}

// ─── Status icon (far-left slot) ─────────────────────────────────────────────
// A not-selected agent is still ``status: 'queued'`` (it never advanced), so it would otherwise
// render the SAME pending dot as a genuinely-waiting agent — contradictory once the run settled.
// ``notSelected`` gets its own distinct SKIPPED glyph (gray ⊘) so the far-left scan column reads
// unambiguously: pending ◌ vs. skipped ⊘ vs. running ⟳ vs. done ✓.
function StatusIcon({ status, notSelected = false }) {
  return (
    <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
      {notSelected ? (
        <MinusCircle className="h-3.5 w-3.5 text-slate-300" aria-hidden />
      ) : status === 'done' ? (
        <Check className="h-3.5 w-3.5 text-emerald-500" aria-hidden />
      ) : status === 'failed' ? (
        // RED (not amber) so a failed agent is unmistakable + distinct from the amber "awaiting".
        <AlertTriangle className="h-3.5 w-3.5 text-red-500" aria-hidden />
      ) : status === 'degraded' ? (
        // AMBER triangle — the agent COMPLETED under a governed limit (policy cap / timeout). A
        // done-family outcome with partial work; distinct from red "failed" and emerald "done".
        <AlertTriangle className="h-3.5 w-3.5 text-amber-500" aria-hidden />
      ) : status === 'awaiting' ? (
        // Spinner (like running) so awaiting rows draw the eye — plus an amber tone so the user
        // can tell "needs me" from "working" at a glance. Both spin per the locked design.
        <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-500" aria-hidden />
      ) : status === 'running' ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" aria-hidden />
      ) : (
        <Circle className="h-2 w-2 text-slate-300" aria-hidden />
      )}
    </span>
  );
}

// ─── Role icon (type, not status) — distinct glyph per role so the user reads the tree at a glance:
//   Lead (router)      → Compass  (indigo)  — decides direction / who works
//   Summarizer (judge) → GitMerge (violet)  — folds the team's work + judges done
//   builder            → (its own violet "builder" chip already; no role icon)
//   agent (worker)     → User     (slate)   — a specialist doing the work
function RoleIcon({ row }) {
  const cls = 'h-3 w-3 shrink-0';
  if (row.isEngineStage) {
    return row.stageKind === 'reducer'
      ? <GitMerge className={`${cls} text-violet-500`} aria-hidden />
      : <Compass className={`${cls} text-indigo-500`} aria-hidden />;
  }
  if (row.isBuilder) return null;   // builder rows carry the explicit "builder" chip already
  return <User className={`${cls} text-slate-400`} aria-hidden />;
}

// ─── Reasoning block (collapsible, in the shared reader) ─────────────────────
/** The selected node's attributed reasoning — the lead's routing rationale ('why these agents')
 *  and/or its node-reducer judge verdict. Collapsible; defaults OPEN while the agent is still
 *  working (so the user sees WHY it's running) and collapsed once it's settled. */
function ReasoningBlock({ reasoning, live, rationaleOnly = false }) {
  const [open, setOpen] = useState(live);
  useEffect(() => { if (live) setOpen(true); }, [live]);
  if (!Array.isArray(reasoning) || reasoning.length === 0) return null;
  // ROUTING-RATIONALE stages (why the lead chose its team / its me-vs-team decision): the engine
  // Group-Lead emits ``lead``; a MANAGER's own tool-free decision (economy) emits
  // ``manager-decision`` (attributed to the manager's own row, "Get Reasoning" on). Both are the
  // "why I routed" story.
  const isRationale = (s) => s === 'lead' || s === 'manager-decision';
  // ``rationaleOnly`` (a ROUTER / Group-Lead node): show ONLY the routing rationale, drop the
  // reducer "verdict" — that accept/keep-going verdict is about the node's SPECIALISTS' fold, not
  // the router itself; on a pure router it misreads as "the lead judged something".
  const filtered = rationaleOnly ? reasoning.filter((r) => isRationale(r.stage)) : reasoning;
  if (filtered.length === 0) return null;
  // Routing rationale first (why the team was chosen), then reducer verdicts (accept / keep going).
  const rank = (s) => (isRationale(s) ? 0 : s === 'reducer' ? 1 : 2);
  const ordered = [...filtered].sort((a, b) => rank(a.stage) - rank(b.stage));
  return (
    <div className="mb-2 rounded border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 px-2 py-1 text-left"
        data-testid="agent-reasoning-toggle"
      >
        {open ? <ChevronDown className="h-3 w-3 text-slate-400" /> : <ChevronRight className="h-3 w-3 text-slate-400" />}
        <Brain className="h-3.5 w-3.5 text-fuchsia-500" aria-hidden />
        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
          Routing rationale
        </span>
      </button>
      {open ? (
        <div className="space-y-1 px-2 pb-1.5" data-testid="agent-reasoning-body">
          {ordered.map((r, i) => (
            <div key={i} className="text-[11px] leading-relaxed text-slate-600">
              {r.stage === 'reducer' && (
                <span className="mr-1 rounded bg-slate-100 px-1 text-[9px] font-medium uppercase tracking-wide text-slate-400">
                  verdict
                </span>
              )}
              {r.text}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ─── One roster line ─────────────────────────────────────────────────────────
function AgentTreeRow({ node, isActive, onSelect, renderRowAction, verbose = false }) {
  const { row, depth, isLast, ancestorsLast } = node;
  const clickable = typeof onSelect === 'function';
  // Engine-stage rows carry an already-clean label ("Network Lead" / "Lead" / "Summarizer") — use
  // it verbatim; only agent ids get humanized (drop trailing "Agent", split camelCase). FALLBACK: on
  // a rehydrated/completed run the stage row's label can be missing or the RAW id (the AGENT_REASONING
  // finalize that set it isn't re-applied on every rebuild) — derive the clean label from the agentId
  // so "__lead__root" never leaks into the tree.
  const label = row.isEngineStage
    ? ((row.label && !row.label.startsWith('__')) ? row.label : engineStageLabel(row.agentId))
    : humanizeAgentName(row.label || row.agentId);
  const action = typeof renderRowAction === 'function' ? renderRowAction(row) : null;
  // ENGAGEMENT (did the Lead pick this agent for THIS run?): selected → bold; notSelected (settled
  // + never ran) → grayed out; still-queued mid-run → normal muted. ``isActive`` = the row the
  // user CLICKED (loads into the shared container) — orthogonal to engagement.
  const engaged = row.selected;
  // CAPPED OUT (manual stage cap, frozen release): agent exists but is disabled this release.
  // Takes visual priority — it's a config state that holds regardless of run routing. Distinct
  // copy from ``notSelected`` (a per-run Lead skip) so the user reads "bigger team, parked" not
  // "the Lead didn't need it".
  const capped = row.cappedOut;
  // ROOM-DISABLED (per-room agent disable, ROOM-SCOPES Phase 1): a room owner parked this agent
  // for THIS room — same slate + strike-through look as ``capped`` (both are deliberate config
  // states) but with its OWN copy so the user knows the switch is per-room + who flipped it.
  // 'branch' = the agent + its whole team are off; 'self' = only this agent (delegates still run).
  const roomDisabled = row.disabledByRoom === 'branch' || row.disabledByRoom === 'self';
  // RESULT REUSE (RESULT-REUSE.md): this agent is bypassed this run because its last result is being
  // reused — read "reused from last run", NEVER the misleading "standby"/queued of a never-run agent.
  const reused = !!row.reusedFromRun && !roomDisabled;
  // STOPPED (B6): a user-cancelled agent is a degrade with the user_cancelled subtype (B2). Read it
  // as "Stopped" (deliberate) — NOT the generic "degraded" (a governed policy-cap). One agent's Stop
  // degrades only ITS branch; siblings finish "Done" + f.synth synthesizes without it (RESULT-REUSE:
  // reuse its last good output next run). Match the subtype, or the B2 reason text as a fallback.
  const stopped = row.status === 'degraded' && (
    row.degradedSubtype === 'user_cancelled'
    || /cancel|stopped by user|user \(stop\)/i.test(String(row.degradedReason || '')));
  const grayed = row.notSelected && !capped && !roomDisabled && !reused;
  // NOT IN TASK (dynamic roster, project task-bound run): the agent exists + is enabled, but the
  // CURRENT task doesn't involve it. Dim it (focus on the relevant agents) — but NOT struck-through
  // (it's not disabled) and lower priority than the deliberate off-states. Known up front (no need
  // to wait for the run to settle, unlike ``grayed``).
  const notInTask = row.notInTask && !capped && !roomDisabled && !grayed && !engaged;
  const dim = capped || grayed || roomDisabled || notInTask || reused;
  // Capped rows keep a READABLE name (slate-500 + strike) — the strike + "disabled this release"
  // label carry the "off" signal, so the text shouldn't fade to near-invisible. The per-run skip
  // (``grayed``) stays the lighter slate-300 (it's a transient run outcome, less to read).
  // Room-disabled rows read exactly like ``capped`` (same slate-500 + strike) — the status text
  // ("disabled by you") differentiates the two off-states.
  // FAILED agents read RED so the user spots which one broke the run at a glance — ranked ABOVE
  // the normal states, but BELOW capped/grayed (those are deliberate config states, not a failure).
  const nameClass = (capped || roomDisabled)
    ? 'text-slate-500 line-through decoration-slate-400/50'
    : grayed
      ? 'text-slate-300 line-through decoration-slate-300/60'
      : row.status === 'failed'
        ? 'text-red-600 font-medium'
        : engaged
          ? 'text-slate-600'
          : row.status === 'running' || row.status === 'awaiting'
            ? 'text-slate-600'
            : notInTask
              ? 'text-slate-300'          // dimmed: not needed for this task (no strike — still enabled)
              : 'text-slate-400';
  // SELECTION is marked by a small dot in the header, NOT by darkening this border. A darker
  // border + ring competed with the run-status chrome for what is only a "which run am I reading"
  // hint; the dot carries the same information at a fraction of the weight, and the border stays
  // constant so the block's edge never shifts emphasis.
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={clickable ? () => onSelect(row.agentId) : undefined}
      className={`flex items-center gap-1.5 rounded px-1.5 py-1 ${clickable ? 'cursor-pointer hover:bg-slate-100' : ''} ${isActive ? 'bg-slate-100' : ''} ${grayed ? 'opacity-70' : ''}`}
      title={
        roomDisabled
          ? `${row.agentId} — disabled for this room by a room owner — enable it from the room tree`
          : capped
            ? `${row.agentId} — part of the team, disabled this release (run depth limited)`
            : grayed
              ? `${row.agentId} — not required for this task`
              : row.agentId
      }
      data-testid="agent-tree-row"
      data-agent-id={row.agentId}
      data-engaged={engaged ? '1' : '0'}
      data-capped={capped ? '1' : '0'}
      data-room-disabled={roomDisabled ? row.disabledByRoom : undefined}
    >
      <TreeGuide depth={depth} isLast={isLast} ancestorsLast={ancestorsLast} />
      <StatusIcon status={row.status} notSelected={dim} />
      {/* ROLE icon (type, not status) — Lead=Compass, Summarizer=GitMerge, agent=User — so the user
          reads WHAT each line is at a glance, distinct from the run-status glyph. */}
      {!dim && <RoleIcon row={row} />}
      <span className={`truncate text-xs ${nameClass}`}>{label}</span>
      {row.isBuilder && !dim && (
        <span className="shrink-0 rounded bg-violet-50 px-1 text-[9px] font-medium uppercase tracking-wide text-violet-500">
          builder
        </span>
      )}
      {/* Builder controls (Stop / awaiting) slot — supplied by the room in Step 4. */}
      {action}
      {/* Only the $/token half is Verbose-gated. ``fallback`` carries LIVE STATUS ("working…",
          "awaiting", the Summarizer heartbeat, "not required") and must ALWAYS render — passing
          ``usage={null}`` yields exactly that fallback, so hiding cost never hides status. */}
      <LaneUsage
        usage={verbose && (row.status === 'done' || row.status === 'degraded') ? row.usage : null}
        fallbackClassName={
          row.status === 'failed' ? 'text-red-600 font-medium'
            : stopped ? 'text-slate-500 font-medium'
            : row.status === 'degraded' ? 'text-amber-600 font-medium'
              : 'text-slate-400'
        }
        fallback={
          // Room-disable copy (locked naming): 'self' = BYPASSED (team stays routable),
          // 'branch' = the node + team are off — both are the room owner's deliberate switch.
          roomDisabled ? (row.disabledByRoom === 'self' ? 'bypassed by you — team active' : 'disabled by you')
            : reused ? 'reused from last run'
            : capped ? 'disabled this release'
              : grayed ? 'not required'
              : row.status === 'awaiting' ? 'awaiting'
                // Network Summarizer heartbeat: progressive synthesis status ("3/8 results in").
                : (row.status === 'running' && row.heartbeatText) ? row.heartbeatText
                  : row.status === 'running' ? 'working…'
                  : row.status === 'failed' ? 'failed'
                    : stopped ? 'Stopped'
                    : row.status === 'degraded' ? 'degraded'
                    // Base/undecided state: the Lead HASN'T engaged this agent yet — it may or may
                    // not be picked. "standby" (ready, may be called) reads truthfully; "queued"
                    // wrongly implied it's about to run. Once the run SETTLES an unengaged row flips
                    // to ``grayed`` → "not required" above (the definitive skip).
                    : row.status === 'done' ? 'done' : 'standby'
        }
      />
    </motion.div>
  );
}

/**
 * @param {Object} props
 * @param {Array<Object>} props.roster       Reducer ``agentRoster`` (flat, run-level).
 * @param {boolean} [props.live]             Run in progress — header shows a spinner.
 * @param {(agentId: string) => void} [props.onRowClick]  Notified when the user picks a row (so
 *   the room can also, e.g., bind a builder's interaction band). Selection is owned here.
 * @param {(row: Object) => React.ReactNode} [props.renderRowAction]  Per-row action slot
 *   (Step-4 Stop button / awaiting affordance). Rendered between the name and the usage chip.
 * @param {React.ReactNode} [props.headerRight]  Extra header content (e.g. the "N awaiting" notice).
 * @param {string|null} [props.selectedAgentId]  External selection (e.g. a docked-tree node click) —
 *   when set, overrides the internal auto-follow so the panel + tree stay in sync.
 * @param {(agentId: string) => void} [props.onAgentSelect]  Notified whenever the effective
 *   selection changes (row click or auto-follow) so the docked tree can highlight the same node.
 */
export default function AgentHierarchyPanel({
  roster, live = false, failed = false, error = '', onRowClick, renderRowAction, headerRight,
  networkId = null, poolId = null, taskId = null, selectedAgentId = null, onAgentSelect = null,
  // Engine-stage cost {lead:{cost,points,modelId,calls}, reducer:{…}} for this run — the summed
  // "All Leads" / "All Reducers" cost lines below the Summary. Null when the run had no engine
  // reasoning (non-generative / not yet started).
  stageCost = null,
  // FOCUS (multi-run): when several runs are on screen, the parent marks the one whose graph is in
  // view. We darken THIS panel's own border (no extra wrapper box) so "the selected block" is the
  // "Network run" panel itself. ``undefined`` (lone run) → default border, no focus styling.
  focused = undefined,
  verbose = false,
  // HOST OWNS THE READER: when the AgentStreamDock is on, the room renders the selected agent's
  // stream/log in the dock instead. In that mode the tree stays a pure LAUNCHER — a row click still
  // fires ``onAgentSelect`` (which opens the dock tab) but this panel does NOT render its own inline
  // detail pane, so there's no second window / no box-in-box. The final Summary block still renders.
  hideReader = false,
}) {
  const allRows = useMemo(() => (Array.isArray(roster) ? roster : []), [roster]);
  // The FINAL synthesis (published by the final reducer as agentName "Summary") is THE deliverable.
  // Pull it OUT of the tree + shared reader: it renders as its own fully-expanded block at the very
  // bottom (unbounded, no scroll window), while every other agent stays a limited box in the shared
  // reader — exactly the architect's model (workers = limited boxes, the deliverable spreads out).
  const summaryRow = useMemo(
    () => allRows.find((r) => String(r.agentId || '').trim().toLowerCase() === 'summary') || null,
    [allRows],
  );
  // The final answer is out when the Summary row is done WITH text (its block renders below).
  const summaryDone = !!(summaryRow && summaryRow.status === 'done'
    && typeof summaryRow.text === 'string' && summaryRow.text.trim());

  // ── STREAMING SUMMARY: fixed-height scroll WINDOW (the anti-jump fix) ──────────────────────
  // While the final answer streams, the block is a FIXED-HEIGHT box with its OWN scrollbar and we
  // pin ITS scroll to the bottom as text arrives. That keeps the PAGE steady — the block does not
  // grow and shove everything down on every chunk (the "push the window then write" jump). The
  // instant it's done, we drop the height cap and render the full deliverable (no inner scroll).
  // This mirrors the ThinkingTerminal, which streams cleanly precisely because it scrolls inside a
  // ``max-h`` box. Markdown stays ON throughout — the jump was the growing unbounded block +
  // page-level auto-scroll, never the markdown itself.
  const summaryStreaming = !!(summaryRow && summaryRow.status !== 'done'
    && typeof summaryRow.text === 'string' && summaryRow.text.length > 0);
  const summaryScrollRef = useRef(null);
  const _summaryLen = (summaryRow && typeof summaryRow.text === 'string') ? summaryRow.text.length : 0;
  useEffect(() => {
    const el = summaryScrollRef.current;
    if (!el) return;
    // WHILE streaming: pin to bottom so the newest text is visible (compose animation), entirely
    // inside this box — the page never scrolls. The box height is a FIXED ceiling either way (see
    // JSX: same max-h streaming vs done), so there is no layout shift when streaming ends; we only
    // move the INNER scroll position.
    if (summaryStreaming) el.scrollTop = el.scrollHeight;
    else el.scrollTop = 0;   // DONE: reset to the top so the reader starts at the beginning
  }, [summaryStreaming, _summaryLen]);
  // NETWORK SUMMARIZER = the run's synthesis HEARTBEAT (no static beacon mid-run). The root node-
  // reducer (``__reducer__root``) legitimately finishes early — it judges layer-1, then the run
  // descends and works for another minute+ before the final synthesis. To the user that early tick
  // read as "the summary is done" long before the actual answer. So while the run is LIVE and the
  // final answer hasn't landed, we HOLD this one top-level row as ``running`` with PROGRESSIVE status
  // ("gathering — 3/8 results in" → "all in — synthesizing…") and tick it done ONLY when the Summary
  // is out. Display-only: derived from real signals (agent done-count + summary presence), never a
  // fake state. Settled/historical runs (``!live`` or summary already out) keep their true statuses.
  const rows = useMemo(() => {
    const base = allRows.filter((r) => r !== summaryRow);
    if (!live || summaryDone) return base;
    // DENOMINATOR = agents ENGAGED THIS RUN, not the whole roster. Every manifest agent is seeded
    // as ``queued`` so the tree renders instantly, so counting all rows showed "0/13" on a run the
    // Lead only ever routed 11 agents into — and on a narrow ask ("just the fundamentals") it would
    // read 0/13 while a single branch ran. An agent counts once it has ADVANCED past ``queued``
    // (AGENT_STARTED arrived), which is exactly the signal ``agentTree`` uses for ``selected``.
    //
    // The denominator GROWS as the BFS descends — at layer 0 we cannot know the reducer will
    // escalate and dispatch 7 more (that decision hasn't been made yet). So this is honestly
    // "engaged so far" — the honest number, and the only one available mid-run.
    const agents = base.filter((r) => !r.isEngineStage);
    const engaged = agents.filter((r) => r.status && r.status !== 'queued');
    const total = engaged.length;
    // A degraded agent IS a result in (it completed under a limit) — count it toward "all in", or
    // the heartbeat would hang on "gathering" waiting for an agent that already finished degraded.
    const doneN = engaged.filter((r) => r.status === 'done' || r.status === 'degraded').length;
    // "All in" prefers the BACKEND's own beat (only the final reducer knows it holds every fold),
    // and falls back to the count when that beat is absent — an older engine, or a dropped
    // SUMMARY_DELTA status, must not leave this row stuck on "gathering" forever. The count
    // fallback can flash "synthesizing…" for a second at a layer break (the last agent of layer N
    // finishes before layer N+1 dispatches); a transient blip is much cheaper than a permanently
    // wrong heartbeat.
    const backendBeat = summaryRow && typeof summaryRow.summaryStatus === 'string'
      ? summaryRow.summaryStatus.trim() : '';
    const allIn = !!backendBeat || (total > 0 && doneN >= total);
    // Prefer the BACKEND's live phase label (from SUMMARY_DELTA status beats: "reading the team's
    // work…" → "composing…") once all agents are in — it reflects the real silent phase the final
    // reducer is in, so there's no dead air during hydration / time-to-first-token.
    // Before the Network Lead has dispatched anything, ``total`` is 0 — "0/0 results in" reads as
    // broken, so say what is actually happening (the Lead is still choosing who runs).
    const beat = allIn
      ? (backendBeat || 'all results in — synthesizing…')
      : (total === 0
        ? (backendBeat || 'routing — selecting the agents for this task…')
        : `gathering — ${doneN}/${total} results in`);
    return base.map((r) => (
      r.agentId === '__reducer__root'
        ? { ...r, status: 'running', heartbeatText: beat, usage: undefined }
        : r
    ));
  }, [allRows, summaryRow, live, summaryDone]);
  const flat = useMemo(() => flattenAgentTree(rows), [rows]);
  const byId = useMemo(() => new Map(rows.map((r) => [String(r.agentId), r])), [rows]);

  // Shared reader selection (agentId). Nothing is selected by default — it's a user choice (row
  // click or docked-tree node click). See the selection effect below.
  const [selectedId, setSelectedId] = useState(null);
  // Copy-to-clipboard for the selected agent's output — hosted in the merged header (the worker
  // window renders headerless, so its old Copy button moved up here alongside Context).
  const [copyHint, setCopyHint] = useState('');
  const copySelected = useCallback((txt) => {
    const t = formatAgentMessageForCopy(txt || '');
    if (!t || !navigator.clipboard) return;
    navigator.clipboard.writeText(t).then(
      () => { setCopyHint('Copied'); window.setTimeout(() => setCopyHint(''), 1500); },
      () => { setCopyHint('Copy failed'); window.setTimeout(() => setCopyHint(''), 1500); },
    );
  }, []);

  const select = useCallback((id) => {
    setSelectedId(id);
    if (typeof onRowClick === 'function') onRowClick(id);
    if (typeof onAgentSelect === 'function') onAgentSelect(id);
  }, [onRowClick, onAgentSelect]);

  // External selection (docked-tree node click) mirrors into the panel — keeps the panel + tree in
  // sync. Still a user-driven pick (the tree click is a user action).
  useEffect(() => {
    if (selectedAgentId && byId.has(selectedAgentId)) {
      setSelectedId(selectedAgentId);
    }
  }, [selectedAgentId, byId]);

  // Selection is a USER CHOICE — nothing is selected by default. We deliberately do NOT auto-follow
  // the running/last-with-text agent: the room pre-seeds the whole tree, and auto-selecting the
  // last agent felt like the UI picked for the user. Only an explicit pick (row click, or a docked-
  // tree node click via ``selectedAgentId``) sets the selection. The one thing we still do is DROP a
  // stale selection if the picked agent leaves the roster (topology/run change) — so the reader
  // doesn't point at a ghost — but we never auto-advance to a new one.
  useEffect(() => {
    setSelectedId((cur) => (cur && byId.has(cur) ? cur : null));
  }, [byId]);

  // Report the effective selection outward (so the docked tree highlights the same node) whenever
  // it changes — covers auto-follow, not just explicit clicks. Guarded to only fire on real change.
  const _lastReported = useRef(null);
  useEffect(() => {
    if (selectedId && selectedId !== _lastReported.current && typeof onAgentSelect === 'function') {
      _lastReported.current = selectedId;
      onAgentSelect(selectedId);
    }
  }, [selectedId, onAgentSelect]);

  const { height, onMouseDown } = useResizable({
    minHeight: 60, defaultHeight: 160,
    maxHeight: typeof window !== 'undefined' ? window.innerHeight * 0.6 : 400,
  });

  if (allRows.length === 0) return null;

  const selected = selectedId ? byId.get(selectedId) : null;
  // Engine-stage rows (Lead router / Node-Reducer judge) title the reader by their JOB, not a
  // humanized agent id: the Lead's row reads "Lead Reasoning" and the reducer's "Reasoning
  // Summarizer" — that's the real work these lines represent.
  const readerLabel = selected
    ? (selected.isEngineStage
        ? (selected.stageKind === 'reducer' ? 'Reasoning Summarizer' : 'Lead Reasoning')
        : humanizeAgentName(selected.label || selected.agentId))
    : null;
  const readerText = selected && typeof selected.text === 'string' ? selected.text : '';
  // For a NOT-selected agent, the "why" lives on its PARENT lead's routing rationale (the lead
  // decided which of its team to engage). Fall back to the agent's own reasoning if present.
  const parentReasoning = selected && selected.parentAgentId
    ? (byId.get(selected.parentAgentId) || {}).reasoning
    : null;
  // Is the selected node a ROUTER (a Group Lead with a team)? A team-owning node has children in
  // the tree (some row names it as parent). In economy mode such a node is a PURE ROUTER: it never
  // does the work — it picks who does — so its reader shows ONLY its routing rationale, never a
  // "standby / may or may not run" line (that's for a not-yet-engaged LEAF) and never a reducer
  // "verdict" (that verdict belongs to its specialists' fold, not to the router).
  const selectedHasTeam = !!(selected && rows.some((r) => r.parentAgentId === selected.agentId));
  const selectedRan = !!(selected
    && ['done', 'stop', 'completed', 'failed', 'degraded'].includes(String(selected.status || '')));

  // The N/N tally counts AGENTS only — engine-stage rows (Lead/Reducer lines) are orchestration
  // work, not agents, so they're excluded from the "done / total" headline (they still render in
  // the tree + reader).
  const agentRows = rows.filter((r) => !r.isEngineStage);
  const doneCount = agentRows.filter((r) => r.status === 'done').length;
  const selectedCount = agentRows.filter((r) => r.selected).length;
  // A builder now renders its terminal HEADERLESS (hideHeader) inside this shared pane — the same
  // lift-and-shift the worker does (hideChrome). So the builder's identity + Context action live in
  // THIS single panel header, exactly like a worker: one header, no embedded-window look. We adapt
  // the header actions by TYPE (worker → Context+Copy on prose; builder → Context on the terminal).
  const selectedIsBuilder = !!(selected && selected.isBuilder);

  // ── AUTO-FOLD the agent tree once synthesis begins ────────────────────────────────────────
  // The tree is the "watch it work" surface. The moment the final summary starts STREAMING the work
  // is done and the answer is what matters — leaving a 13-row tree above it buries the thing the
  // user asked for. So fold to just the header + summary, with an explicit toggle to reopen (and
  // re-close). Historical runs open folded for the same reason: you scroll back for the ANSWER.
  //
  // ``summaryStarted`` is deliberately the FIRST TOKEN, not completion — folding at the start means
  // the summary streams into a clean block instead of the layout jumping when it finishes.
  const summaryStarted = !!(summaryRow && typeof summaryRow.text === 'string'
    && summaryRow.text.length > 0);
  // null = follow the auto rule; true/false = the user has taken explicit control for this run.
  const [treeOpenOverride, setTreeOpenOverride] = useState(null);
  const treeOpen = treeOpenOverride == null ? !summaryStarted : treeOpenOverride;
  // A NEW run in the same block resets the override, so the next run's tree is visible again
  // (otherwise a user who folded run 1 would never see run 2 work).
  useEffect(() => { if (!summaryStarted) setTreeOpenOverride(null); }, [summaryStarted]);

  // WHOLE-RUN COST — computed here (not inline at the bottom) because the headline now lives in
  // the HEADER while the per-stage breakdown below is gated behind ``verbose``. One computation,
  // two consumers, so the header total can never disagree with the breakdown.
  const runTotal = useMemo(
    () => computeRunTotal(rows, stageCost, summaryRow?.usage),
    [rows, stageCost, summaryRow],
  );

  // DEGRADED (governed stop): a run that COMPLETED but ≥1 agent stopped under a policy cap / timeout.
  // Distinct from ``failed`` (which aborts the run) — the run still produced a whole result, just with
  // partial contributions. Surface an amber header + the reason(s) so it's honest, not a silent green.
  // Derived from the roster rows the reducer marked ``status:'degraded'`` (with a ``degradedReason``).
  const degradedInfo = useMemo(() => {
    const rows_ = Array.isArray(allRows) ? allRows.filter((r) => r && r.status === 'degraded') : [];
    if (!rows_.length) return null;
    const reasons = rows_
      .map((r) => String(r.degradedReason || '').trim())
      .filter(Boolean);
    return { count: rows_.length, reason: reasons[0] || '', reasons };
  }, [allRows]);
  const isDegraded = !failed && !!degradedInfo;

  // ONE FLAT WHITE SURFACE, NO OUTER FRAME. The fill is set ONCE here on the outer element and
  // every inner section stays unfilled so it shows through — that is what makes the block read as
  // a single surface rather than a stack of tiled cards. No ``shadow-sm`` and no outer ``border``:
  // both drew a box around the run, and a box is exactly what makes it a separate window sitting
  // on the conversation instead of part of it. The internal ``border-b``/``border-t`` rules stay —
  // they separate the run's OWN sections (header / tree / summary), which is a real boundary.
  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-w-0 overflow-hidden rounded-lg bg-white"
      data-testid="agent-hierarchy-panel"
      data-focused={focused === true ? '1' : focused === false ? '0' : undefined}
    >
      {/* Header — run status, selected agent, N/N, plus the room's "N awaiting" notice slot. */}
      <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-1.5">
        {live
          ? <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" aria-hidden />
          : failed
            ? <AlertTriangle className="h-3.5 w-3.5 text-red-500" aria-hidden />
            : isDegraded
              ? <AlertTriangle className="h-3.5 w-3.5 text-amber-500" aria-hidden />
              : <Check className="h-3.5 w-3.5 text-emerald-500" aria-hidden />}
        <span className="text-[11px] font-semibold text-slate-800">
          {live ? 'Network running'
            : failed ? 'Network run failed'
              : isDegraded ? 'Network run — degraded'
                : 'Network run'}
        </span>
        {/* SELECTION marker — a 4px dot, nothing more. Replaces the earlier border-darkening +
            ring, which competed with the run-status chrome for what is only a "this is the run
            you're reading" hint. Rendered only when focus is EXPLICIT (true), never for the
            undefined/single-run case, so a room with one run shows no marker at all. */}
        {focused === true && (
          <span
            className="h-1 w-1 shrink-0 rounded-full bg-slate-400"
            data-testid="run-focus-dot"
            title="Selected run"
            aria-label="Selected run"
          />
        )}
        {/* HEADLINE RUN COST — always visible, right beside the selection dot, even when Verbose is
            OFF. The detailed per-agent/per-stage breakdown below is developer telemetry and hides
            with Verbose; the single number a user might actually care about must not hide with it. */}
        {(runTotal.cost > 0 || runTotal.points > 0) && (
          <span
            className="shrink-0 text-[10px] tabular-nums font-mono text-slate-500"
            data-testid="run-total-cost-header"
            title={`Total run cost${runTotal.points > 0 ? ` · ${runTotal.points.toLocaleString()} pts` : ''}`}
          >
            {runTotal.cost >= 0.01 ? `$${runTotal.cost.toFixed(2)}` : `$${runTotal.cost.toFixed(4)}`}
          </span>
        )}
        {/* Name shown for BOTH worker and builder — the builder's terminal is now headerless, so
            this single header carries its identity too (no double-name). */}
        {readerLabel && <span className="truncate text-[10px] text-slate-400">· {readerLabel}</span>}
        <span className="ml-auto shrink-0 flex items-center gap-2">
          {/* Header actions ADAPT to the selected agent's TYPE — one header, its window renders
              headerless below (worker: hideChrome; builder: hideHeader), so there's never two.
                • WORKER with prose → Context (prompt-capture) + Copy
                • BUILDER           → Context (prompt-capture) for the builder's kick */}
          {/* These actions belong to the inline reader — hidden when the host (dock) owns it. */}
          {hideReader ? null : selected && !selected.isBuilder && readerText ? (
            <>
              <PromptCaptureLink
                networkId={networkId}
                source="worker"
                agent={selected.label || selected.agentId}
                pool={poolId}
                task={selected.runId || taskId}
              />
              <button
                type="button"
                onClick={() => copySelected(readerText)}
                className={`inline-flex items-center justify-center rounded p-1 text-slate-500 transition-colors hover:bg-slate-100/80 hover:text-slate-800 ${
                  copyHint === 'Copied' ? 'text-emerald-600' : copyHint === 'Copy failed' ? 'text-red-600' : ''
                }`}
                aria-label={copyHint || 'Copy message'}
                title={copyHint || 'Copy message'}
              >
                <Copy className="h-3 w-3 shrink-0" aria-hidden />
              </button>
            </>
          ) : selected && selected.isBuilder ? (
            <PromptCaptureLink
              networkId={networkId}
              source="builder"
              agent={selected.label || selected.agentId}
              run={selected.runId}
            />
          ) : null}
          {headerRight}
        </span>
      </div>

      {/* FAILURE REASON — shown directly under the "Network run failed" header so the user sees WHY
          (e.g. "hit its maximum-spend cap ($2)…") instead of a bare red header + a hunt through the
          agent build log. Only on a failed run WITH a captured reason; success/live render nothing. */}
      {failed && error ? (
        <div
          className="border-b border-red-100 bg-red-50 px-3 py-1.5"
          data-testid="run-failure-reason"
          role="alert"
        >
          <p className="text-[11px] leading-relaxed text-red-700 whitespace-pre-wrap break-words">
            {error}
          </p>
        </div>
      ) : isDegraded && degradedInfo?.reason ? (
        /* DEGRADED REASON — amber (not red): the run completed under a governed limit. Shows WHY
           (which cap / timeout) inline, so a partial result is honest rather than a silent green. */
        <div
          className="border-b border-amber-100 bg-amber-50 px-3 py-1.5"
          data-testid="run-degraded-reason"
          role="status"
        >
          <p className="text-[11px] leading-relaxed text-amber-700 whitespace-pre-wrap break-words">
            {degradedInfo.count > 1
              ? `${degradedInfo.count} agents stopped under a governed limit. ${degradedInfo.reason}`
              : degradedInfo.reason}
          </p>
        </div>
      ) : null}

      {/* Shared container — the ONE pane that ADAPTS to the selected agent's TYPE:
            • builder, live   → BuilderStreamTerminal (its own CLI/terminal chrome + resize)
            • builder, done   → BuilderLogCard (S3 transcript replay, terminal look)
            • worker (any)    → text pane (reasoning block + final prose, fixed-height + resize)
          Same container, different renderer — no per-agent windows. Hidden when the run has no
          non-summary agents (only the final Summary block renders below). */}
      {/* Reader also hides when the tree is FOLDED — a folded run is header + summary only,
          so leaving a selected agent's output pane above the answer would defeat the fold. */}
      {rows.length > 0 && treeOpen && !hideReader ? (
        selected && selected.notSelected ? (
          // NOT-SELECTED agent → explain WHY (the Lead routed around it) + show the lead's full
          // routing rationale so the user reads the reasoning. No output/terminal for it.
          <div
            style={{ height: `${height}px` }}
            className="min-w-0 overflow-y-auto overflow-x-hidden px-3 py-2 [scrollbar-gutter:stable]"
            data-testid="agent-not-selected"
          >
            <div className="mb-2 rounded border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-500">
              <span className="font-medium text-slate-600">{readerLabel}</span> was <span className="font-medium">not required</span> for this task —
              the lead didn’t route any work to it this run.
            </div>
            {Array.isArray(parentReasoning) && parentReasoning.length > 0 ? (
              <div className="rounded border border-slate-200 bg-white">
                <div className="flex items-center gap-1.5 px-2 py-1">
                  <Brain className="h-3.5 w-3.5 text-fuchsia-500" aria-hidden />
                  <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                    Lead’s routing rationale
                  </span>
                </div>
                <div className="space-y-1 px-2 pb-1.5">
                  {parentReasoning.map((r, i) => (
                    <div key={i} className="text-[11px] leading-relaxed text-slate-600">{r.text}</div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-[11px] text-slate-400">No routing rationale was recorded for this run.</div>
            )}
          </div>
        ) : selected && selected.isBuilder
          && (selected.status === 'running' || selected.status === 'awaiting')
          && Array.isArray(selected.builderLines) && selected.builderLines.length > 0 ? (
            // LIVE builder → its terminal, HEADERLESS (its name + Context live in the panel header
            // above — one header, like the worker). Brings its own height/resize, so NOT boxed here.
            // KEY per builder: reset the terminal's internal UI state (expanded/collapsed/scroll)
            // when the user switches between LIVE builders, so one builder's expand state doesn't
            // bleed onto the next. (Content is prop-driven and already refreshes; this is the chrome.)
            <BuilderStreamTerminal
              key={`live:${selected.runId || ''}:${selected.agentId || ''}`}
              lines={selected.builderLines}
              status="live"
              builderPhases={[]}
              agentName={selected.agentId}
              networkId={networkId}
              runId={selected.runId}
              hideHeader
            />
          ) : selected && selected.isBuilder && (
                (selected.builderCompletion
                  && (selected.builderCompletion.logKey || selected.builderCompletion.eventsKey))
                || (typeof selected.text === 'string' && selected.text.trim())
              ) ? (
                // DONE builder → its transcript replay (terminal look). AUTO-LOAD: the user clicked
                // this row to see the builder — fetch the transcript straight into the reader
                // instead of showing a bare header with a redundant "Load session" button.
                // KEY (stale-transcript fix): the reader reuses ONE card slot across builder
                // selections. Without a per-builder key React keeps the SAME BuilderLogCard
                // instance, so its internal ``events`` state (builder #1's transcript) survives the
                // prop swap and the ``!events`` auto-load guard blocks the refetch → the first
                // builder's log stays on screen. Keying by the selected builder's identity remounts
                // the card (fresh ``events=null``) so it loads the clicked builder. (Workers don't
                // hit this — they render from a ``readerText`` prop, no internal fetched state.)
                <div className="px-0">
                  {/* CLAIM-CHECK FIRST: the EXACT text f.synth folds (the builder's AGENT_FINAL_RESPONSE,
                      published by engine_agent). The user must see WHAT was contributed — not only the
                      transcript. The full run log (BuilderLogCard) sits BESIDE/below it. */}
                  {readerText ? (
                    <div className="px-3 pt-2 pb-1">
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                        Output (used by the synthesizer)
                      </div>
                      <AgentResponsePanel
                        agentName={selected.label || selected.agentId}
                        text={readerText}
                        contentKey={`${selected.agentId}:claimcheck`}
                        usage={selected.usage}
                        networkId={networkId}
                        poolId={poolId}
                        taskId={selected.runId || taskId}
                        hideChrome
                      />
                      {/* "Agent logs" label ONLY when there IS a transcript to show below. A
                          transcript-less builder (no logKey/eventsKey — e.g. its work ran without a
                          streamed session) still shows the claim-check above; it just has no log card. */}
                      {selected.builderCompletion
                        && (selected.builderCompletion.logKey || selected.builderCompletion.eventsKey) ? (
                        <div className="mt-2 mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                          Agent logs
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {/* Transcript card only when a transcript exists — its key/props deref builderCompletion,
                      which is null for a transcript-less builder (would crash if rendered unconditionally). */}
                  {selected.builderCompletion
                    && (selected.builderCompletion.logKey || selected.builderCompletion.eventsKey) ? (
                    <BuilderLogCard
                      key={`${selected.runId || ''}:${selected.agentId || ''}:${selected.builderCompletion.eventsKey || selected.builderCompletion.logKey || ''}`}
                      completion={selected.builderCompletion}
                      agentName={selected.agentId}
                      networkId={networkId}
                      runId={selected.runId}
                      autoLoad
                      hideHeader
                    />
                  ) : null}
                </div>
              ) : selected && readerText ? (
                // WORKER with output → the REAL AgentResponsePanel body (lift-and-shift), rendered
                // with ``hideChrome`` so it nests UNDER the panel's SINGLE header — no second header,
                // no box-in-box. Its Context/Copy actions are hosted in the panel header (see the
                // header block above, via ``selectedActions``). Usage bar + draggable resize kept.
                // The attributed routing/verdict reasoning renders above it (tree-only concept).
                <div className="px-0">
                  {Array.isArray(selected.reasoning) && selected.reasoning.length > 0 ? (
                    <div className="px-3 pt-2">
                      <ReasoningBlock
                        reasoning={selected.reasoning}
                        live={selected.status === 'running' || selected.status === 'awaiting'}
                        rationaleOnly={selectedHasTeam}
                      />
                    </div>
                  ) : null}
                  <AgentResponsePanel
                    agentName={selected.label || selected.agentId}
                    text={readerText}
                    contentKey={selected.agentId}
                    usage={selected.usage}
                    networkId={networkId}
                    poolId={poolId}
                    taskId={selected.runId || taskId}
                    hideChrome
                  />
                </div>
              ) : (
                // Selected but NO output yet. Three cases:
                //   • awaiting → waiting on the user
                //   • running  → actively working
                //   • standby  → the Lead HASN'T engaged it yet (may or may not be picked). This is
                //     the base state before the run settles; unlike a settled ``notSelected`` row it
                //     could still run. We say so honestly ("not been engaged yet") — never the old
                //     "No output yet." (which read like a bug) — and show the Lead's routing
                //     rationale so the user sees WHY, the same "why" the settled skip surfaces.
                <>
                  {/* COLLAPSED BY DEFAULT: with nothing selected the pane reserved ``height`` px
                      (160 by default) to show a single "Select an agent…" line, pushing the tree
                      down and wasting the most valuable space in the block. Render the pane only
                      once there IS something to read; the tree sits directly under the header until
                      then. The resize handle is hidden too — there is nothing to resize. */}
                  {selected ? (
                  <div
                    style={{ height: `${height}px` }}
                    className="min-w-0 overflow-y-auto overflow-x-hidden px-3 py-2 text-[12px] leading-relaxed text-slate-800 [scrollbar-gutter:stable]"
                  >
                    <ReasoningBlock
                      reasoning={selected.reasoning}
                      live={selected.status === 'running' || selected.status === 'awaiting'}
                      rationaleOnly={selectedHasTeam}
                    />
                    {(selected.status === 'running' || selected.status === 'awaiting') ? (
                        <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                          <span>
                            {selected.status === 'awaiting'
                              ? 'Waiting for your response…'
                              : `${readerLabel} is working…`}
                          </span>
                        </div>
                      ) : selectedRan ? (
                        // RAN — this node already executed (a Group Lead that ROUTED, or a node with
                        // no separate output). NOT standby. Its ``ReasoningBlock`` above already
                        // shows the routing rationale; add a one-line summary of what it did. NEVER
                        // the "hasn't been engaged / may or may not run" copy — it DID run.
                        <div data-testid="agent-ran" className="text-[11px] text-slate-500">
                          {selectedHasTeam
                            ? `${readerLabel} routed this to its specialist team.`
                            : `${readerLabel} finished — no separate output to show.`}
                        </div>
                      ) : (
                        // STANDBY — genuinely not engaged yet (queued). Explain + surface the Lead's
                        // routing rationale so the user sees WHY it may or may not run.
                        <div data-testid="agent-standby">
                          <div className="mb-2 rounded border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-500">
                            <span className="font-medium text-slate-600">{readerLabel}</span> hasn’t
                            been engaged yet — it’s on <span className="font-medium">standby</span>.
                            The lead brings an agent in only when the task needs it, so this one may
                            or may not run.
                          </div>
                          {Array.isArray(parentReasoning) && parentReasoning.length > 0 ? (
                            <div className="rounded border border-slate-200 bg-white">
                              <div className="flex items-center gap-1.5 px-2 py-1">
                                <Brain className="h-3.5 w-3.5 text-fuchsia-500" aria-hidden />
                                <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                                  Lead’s routing rationale
                                </span>
                              </div>
                              <div className="space-y-1 px-2 pb-1.5">
                                {parentReasoning.map((r, i) => (
                                  <div key={i} className="text-[11px] leading-relaxed text-slate-600">{r.text}</div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="text-[11px] text-slate-400">
                              The lead hasn’t explained its routing for this run yet.
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                  ) : null}
                  {selected ? (
                  <div
                    onMouseDown={onMouseDown}
                    className="flex items-center justify-center h-2 cursor-row-resize border-t border-slate-200/80 hover:bg-slate-100 transition-colors group"
                  >
                    <GripHorizontal className="h-2.5 w-3.5 text-slate-400 group-hover:text-slate-600" />
                  </div>
                  ) : null}
                </>
              )
      ) : null}

      {/* Roster — the tree. One non-expanding line per agent; click to load into the reader. */}
      {rows.length > 0 ? (
        <div className="border-t border-slate-200 px-2 py-1.5">
          {/* The "Agents" line is the FOLD TOGGLE. Whole row is the hit target (not just the
              chevron) so it's easy to hit, and it keeps its progress counter in both states — a
              folded run still tells you how many agents ran. */}
          <button
            type="button"
            onClick={() => setTreeOpenOverride(!treeOpen)}
            className="mb-1 flex w-full items-center gap-1.5 rounded text-left hover:bg-slate-50"
            aria-expanded={treeOpen}
            data-testid="agents-fold-toggle"
            title={treeOpen ? 'Hide the agent tree' : 'Show the agent tree'}
          >
            {treeOpen
              ? <ChevronDown className="h-3 w-3 shrink-0 text-slate-400" aria-hidden />
              : <ChevronRight className="h-3 w-3 shrink-0 text-slate-400" aria-hidden />}
            <Clock className="h-3 w-3 text-slate-400" aria-hidden />
            <span className="text-[11px] font-medium text-slate-600">Agents</span>
            {/* Dynamic progress lives HERE (not the header) — done/total, and engaged-count when a
                seeded topology means some agents weren't required this run. */}
            <span className="text-[10px] text-slate-400">
              {selectedCount > 0 && selectedCount < agentRows.length
                ? `${doneCount}/${selectedCount} done · ${agentRows.length} total`
                : `${doneCount}/${agentRows.length}`}
            </span>
          </button>
          {treeOpen && (
          <div className="space-y-0.5" data-testid="agent-tree-list">
            <AnimatePresence initial={false}>
              {flat.map((node) => (
                <AgentTreeRow
                  key={node.row.agentId}
                  node={node}
                  isActive={selectedId === node.row.agentId}
                  onSelect={select}
                  renderRowAction={renderRowAction}
                  verbose={verbose}
                />
              ))}
            </AnimatePresence>
          </div>
          )}
        </div>
      ) : null}

      {/* FINAL SUMMARY — the deliverable. Its own fully-expanded block at the very bottom (no scroll
          window, no resize grip): the synthesized answer of all the work should spread out, unlike
          the per-agent limited boxes above. Rendered only once it has text. */}
      {summaryRow && typeof summaryRow.text === 'string' && summaryRow.text.length > 0 ? (
        <div
          className={treeOpen ? 'border-t-2 border-emerald-200' : 'border-t border-slate-200'}
          data-testid="agent-summary-block"
        >
          {/* The "Summary" label bar FOLDS WITH THE AGENT TREE. It is run chrome — a title, a
              status beat and a cost chip — and once the answer is on screen the answer identifies
              itself; a labelled emerald bar over it just re-frames the run as a live process that
              has already finished. So the folded state is exactly the deliverable: header + prose.
              Expanding the tree brings it back, because then you ARE inspecting the run.

              The block's own top border goes neutral when folded, since the emerald ``border-t-2``
              only existed to pair with this bar. */}
          {treeOpen ? (
          <div className="flex items-center gap-2 border-b border-slate-200 bg-emerald-50/60 px-3 py-1.5"
            data-testid="agent-summary-header">
            <Sparkles className="h-3.5 w-3.5 text-emerald-500" aria-hidden />
            <span className="text-[11px] font-semibold text-slate-800">Summary</span>
            {/* COMPOSING indicator: while the synthesis streams (running, not yet the final record),
                show a live "writing…" beat so the block reads as actively composing, not done.
                When folded, the blinking tail cursor below carries this signal instead. */}
            {summaryRow.status !== 'done' ? (
              <span className="flex items-center gap-1 text-[10px] text-emerald-600">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                <span>writing the final answer…</span>
              </span>
            ) : (
              <span className="text-[10px] text-slate-400">· final answer</span>
            )}
            {/* Final-synthesis cost chip in the header (model + $ + pts) — parity with every worker
                row. The full breakdown is the UsageBar below. */}
            <span className="ml-auto shrink-0">
              <LaneUsage usage={summaryRow.usage} fallback={null} />
            </span>
          </div>
          ) : null}
          {/* The box height treatment is IDENTICAL streaming vs done — a fixed viewport-relative
              CEILING (``max-h-[70vh]`` + inner scroll), NEVER un-capped. This is the crux of the
              no-jump behavior: un-capping the box at completion (420px → full content height) is a
              one-frame reflow that ``motion.div layout`` animates as a jump-up-and-settle. Keeping
              the SAME ceiling in both phases means completion changes nothing about the box → zero
              layout shift → the text just stops moving. The ceiling was ``420px`` (too short — a
              long answer was trapped in a small window with a big gap down to the composer); ``70vh``
              lets the deliverable fill most of the viewport, reaching toward the prompt box, while
              staying a STABLE fixed height. While streaming we pin to the bottom (compose animation);
              the instant it's done we reset to the top so the reader starts at the beginning. */}
          <div
            ref={summaryScrollRef}
            className="min-w-0 px-3 py-2 text-[12px] leading-relaxed text-slate-800 overflow-x-hidden max-h-[70vh] overflow-y-auto [scrollbar-gutter:stable]"
          >
            <AgentMessageContent text={summaryRow.text} className="!mt-0"
              streaming={summaryRow.status !== 'done'} />
            {/* Blinking cursor at the tail while streaming — the compose animation. */}
            {summaryRow.status !== 'done' ? (
              <span className="ml-0.5 inline-block h-3.5 w-[6px] animate-pulse rounded-sm bg-emerald-400 align-middle" aria-hidden />
            ) : null}
          </div>
          {verbose && summaryRow.status === 'done' ? <UsageBar usage={summaryRow.usage} /> : null}
          {/* ENGINE-STAGE COST BREAKDOWN — the orchestration spend that has no roster row of its
              own: all Lead (routing) calls + all Reducer (judge) calls, summed across the run.
              The Summary's own cost is in its UsageBar above; the workers' are on their rows. */}
          {verbose && (
            <div className="border-t border-slate-200" data-testid="stage-cost-breakdown">
              <StageCostLine label="All Leads (routing)" usage={stageCost?.lead} />
              <StageCostLine label="All Reducers (judging)" usage={stageCost?.reducer} />
            </div>
          )}
          {/* TOTAL RUN COST — the detailed footer line. The same number is ALWAYS in the header
              (see run-total-cost-header); this fuller row (with points) belongs with the rest of
              the breakdown, so it hides with Verbose rather than duplicating the header. */}
          {verbose && (() => {
            const total = runTotal;
            if (total.cost === 0 && total.points === 0) return null;
            return (
              <div className="flex items-center gap-1.5 border-t-2 border-emerald-200 bg-emerald-50/40 px-3 py-1.5 text-[11px] tabular-nums font-mono"
                data-testid="run-total-cost">
                <span className="font-semibold text-slate-700">Total run cost</span>
                <span className="ml-auto flex items-center gap-2">
                  <span className="font-semibold text-slate-800">
                    {total.cost >= 0.01 ? `$${total.cost.toFixed(2)}` : `$${total.cost.toFixed(4)}`}
                  </span>
                  {total.points > 0 && <span className="text-slate-500">{total.points.toLocaleString()} pts</span>}
                </span>
              </div>
            );
          })()}
        </div>
      ) : null}
    </motion.div>
  );
}
